// Connects a player to a PlaybackTracker. Each returns a function that disconnects it and
// reports how far playback got - call it when the player goes away or the content changes.
//
// Ported from the backend's activity-tracking-frontend-guide.md (version 2), with one change:
// trackVimeoPlayer() can be told not to pick up playback already under way (see below).

import { PlaybackTracker } from "./playback";

/**
 * Works out what an <audio> element is playing, from its src. null when it is nothing we track.
 *
 * @typedef {(media: HTMLMediaElement) => import("./playback").PlayableContent | null} ContentResolver
 */

/**
 * A Vimeo player (@vimeo/player), however it was created.
 *
 * Pass `pickUpPlayback: false` when the player is switching to `content` with loadVideo():
 * until the new video has loaded, the player still reports the old one as playing, and
 * picking that up would credit the old video's position to the new one. The new video's
 * own 'play' event starts its playback instead.
 *
 * @param {import("@vimeo/player").default} player
 * @param {import("./playback").PlayableContent} content
 * @param {import("./client").ActivityClient} client
 * @param {{ pickUpPlayback?: boolean }} [options]
 */
export function trackVimeoPlayer(player, content, client, { pickUpPlayback = true } = {}) {
  const tracker = new PlaybackTracker(client, content);
  let detached = false;

  const onPlay = (data) => tracker.play(data.seconds, data.duration);
  const onTimeUpdate = (data) => tracker.time(data.seconds, data.duration);
  const onSeeked = (data) => tracker.seeked(data.seconds);
  const onPause = (data) => tracker.pause(data.seconds);
  const onEnded = (data) => tracker.ended(data.seconds);
  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      tracker.leave();
    }
  };

  player.on("play", onPlay);
  player.on("timeupdate", onTimeUpdate);
  player.on("seeked", onSeeked);
  player.on("pause", onPause);
  player.on("ended", onEnded);
  document.addEventListener("visibilitychange", onVisibilityChange);

  // Connected after playback had already started - autoplay can beat React's effects.
  if (pickUpPlayback) {
    Promise.all([player.getPaused(), player.getCurrentTime(), player.getDuration()])
      .then(([paused, seconds, duration]) => {
        if (!detached && !paused) {
          tracker.play(seconds, duration);
        }
      })
      .catch(() => undefined);
  }

  return () => {
    detached = true;
    tracker.leave();
    document.removeEventListener("visibilitychange", onVisibilityChange);

    // The player may already have been destroyed by the time this runs.
    try {
      player.off("play", onPlay);
      player.off("timeupdate", onTimeUpdate);
      player.off("seeked", onSeeked);
      player.off("pause", onPause);
      player.off("ended", onEnded);
    } catch {
      // nothing left to detach from
    }
  };
}

/**
 * An <audio> or <video> element. Pass the content it plays, or - for a player that plays a
 * list of tracks through one element - a function that works it out from the element.
 * That function is asked when playback starts, so it always sees the track actually loaded,
 * however quickly the player moves on to the next one.
 *
 * @param {HTMLMediaElement} media
 * @param {import("./playback").PlayableContent | ContentResolver} content
 * @param {import("./client").ActivityClient} client
 */
export function trackMediaElement(media, content, client) {
  const resolve = typeof content === "function" ? content : () => content;
  let tracker = null;
  let trackedKey = null;
  let playing = false;

  const duration = () => (Number.isFinite(media.duration) ? media.duration : undefined);

  const drop = () => {
    tracker?.leave();
    tracker = null;
    trackedKey = null;
  };

  // The tracker for whatever is loaded now: a new one when the content changed.
  const current = () => {
    const found = resolve(media);
    if (found === null) {
      drop();
      return null;
    }

    const key = `${found.type}:${found.id}`;
    if (tracker === null || key !== trackedKey) {
      drop();
      tracker = new PlaybackTracker(client, found);
      trackedKey = key;
    }

    return tracker;
  };

  const onPlay = () => {
    playing = true;
    current()?.play(media.currentTime, duration());
  };
  const onTimeUpdate = () => {
    if (!playing && !media.paused) {
      onPlay(); // connected after playback had already started
      return;
    }
    tracker?.time(media.currentTime, duration());
  };
  const onSeeked = () => tracker?.seeked(media.currentTime);
  // The element pauses itself just before it ends; 'ended' reports that one.
  const onPause = () => {
    playing = false;
    if (!media.ended) {
      tracker?.pause(media.currentTime);
    }
  };
  const onEnded = () => {
    playing = false;
    tracker?.ended(media.currentTime);
  };
  // A new source was loaded into the element - the next track. Whatever played before has stopped.
  const onEmptied = () => {
    playing = false;
    drop();
  };
  const onVisibilityChange = () => {
    if (document.visibilityState === "hidden") {
      tracker?.leave();
    }
  };

  media.addEventListener("play", onPlay);
  media.addEventListener("timeupdate", onTimeUpdate);
  media.addEventListener("seeked", onSeeked);
  media.addEventListener("pause", onPause);
  media.addEventListener("ended", onEnded);
  media.addEventListener("emptied", onEmptied);
  document.addEventListener("visibilitychange", onVisibilityChange);

  return () => {
    drop();
    media.removeEventListener("play", onPlay);
    media.removeEventListener("timeupdate", onTimeUpdate);
    media.removeEventListener("seeked", onSeeked);
    media.removeEventListener("pause", onPause);
    media.removeEventListener("ended", onEnded);
    media.removeEventListener("emptied", onEmptied);
    document.removeEventListener("visibilitychange", onVisibilityChange);
  };
}
